/**
 * Routes of docs/08 §7, one line per route: each feature replaces its own page file under
 * `features/<feature>/` without reorganising this file. Pages are lazy-loaded (default export).
 */
import type { ComponentType } from 'react';
import { Navigate, createBrowserRouter } from 'react-router';
import type { RouteObject } from 'react-router';
import { AppShell } from './components/layout/AppShell';
import { NotFoundPage } from './components/layout/NotFoundPage';
import { RouteErrorPage } from './components/layout/RouteErrorPage';
import { SplashScreen } from './components/layout/StatusScreens';
import { SettingsLayout } from './components/layout/SettingsLayout';
import { RequireAuth } from './lib/auth';

const page = (load: () => Promise<{ default: ComponentType }>) => async () => ({
  Component: (await load()).default,
});

export const routes: RouteObject[] = [
  {
    path: '/login',
    hydrateFallbackElement: <SplashScreen />,
    lazy: page(() => import('./features/auth/LoginPage')),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/lock',
    hydrateFallbackElement: <SplashScreen />,
    lazy: page(() => import('./features/auth/LockPage')),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/cambio-password',
    hydrateFallbackElement: <SplashScreen />,
    lazy: page(() => import('./features/auth/ChangePasswordPage')),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/',
    hydrateFallbackElement: <SplashScreen />,
    errorElement: <RouteErrorPage />,
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, lazy: page(() => import('./features/dashboard/DashboardPage')) },
      { path: 'movimenti', lazy: page(() => import('./features/movements/MovementsPage')) },
      { path: 'movimenti/nuovo', lazy: page(() => import('./features/movements/MovementNewPage')) },
      {
        path: 'movimenti/:id',
        lazy: page(() => import('./features/movements/MovementDetailPage')),
      },
      { path: 'giacenze', lazy: page(() => import('./features/stock/StockPage')) },
      { path: 'report', lazy: page(() => import('./features/reports/ReportsPage')) },
      { path: 'report/nuovo', lazy: page(() => import('./features/reports/ReportNewPage')) },
      { path: 'report/tutti', lazy: page(() => import('./features/reports/ReportBatchPage')) },
      { path: 'semaforo', lazy: page(() => import('./features/traffic/TrafficPage')) },
      { path: 'chiusura/:day', lazy: page(() => import('./features/closure/ClosurePage')) },
      { path: 'pianificazione', lazy: page(() => import('./features/planning/PlanningPage')) },
      { path: 'checklist/:day', lazy: page(() => import('./features/checklist/ChecklistPage')) },
      { path: 'archivio', lazy: page(() => import('./features/archive/ArchivePage')) },
      {
        path: 'impostazioni',
        element: <SettingsLayout />,
        children: [
          { index: true, element: <Navigate to="generali" replace /> },
          { path: 'generali', lazy: page(() => import('./features/settings/SettingsGeneralPage')) },
          { path: 'punti', lazy: page(() => import('./features/points/PointsPage')) },
          { path: 'giorni', lazy: page(() => import('./features/days/DaysPage')) },
          { path: 'persone', lazy: page(() => import('./features/persons/PersonsPage')) },
          { path: 'utenti', lazy: page(() => import('./features/users/UsersPage')) },
          { path: 'tipi', lazy: page(() => import('./features/movement-types/MovementTypesPage')) },
          { path: 'import', lazy: page(() => import('./features/import/ImportPage')) },
        ],
      },
      { path: 'audit', lazy: page(() => import('./features/audit/AuditPage')) },
      { path: 'esporta', lazy: page(() => import('./features/export/ExportPage')) },
      { path: 'scheda', lazy: page(() => import('./features/texts/TextsPage')) },
      { path: 'sync', lazy: page(() => import('./features/sync/SyncPage')) },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

/** Created by app.tsx (not at import time, so the route table can be tested outside the browser). */
export function createAppRouter() {
  return createBrowserRouter(routes);
}
